import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { FiChevronDown } from 'react-icons/fi';
import { formatCurrency } from '../../utils/currencyFormatter';
import { useCurrency } from '../../context/CurrencyContext';
import { getColorCode } from '../../utils/colorHelper';
import { trackInitiateCheckout } from '../../utils/facebookPixel';

const PROVINCES = [
    'Central', 'Eastern', 'North Central', 'Northern',
    'North Western', 'Sabaragamuwa', 'Southern', 'Uva', 'Western'
];

const COUNTRIES = [
    'Sri Lanka', 'India', 'United States', 'United Kingdom', 'Australia',
    'Canada', 'United Arab Emirates', 'Qatar', 'Kuwait', 'Saudi Arabia',
    'Oman', 'Singapore', 'Malaysia', 'Japan', 'France', 'Germany',
    'Italy', 'Spain', 'New Zealand', 'Maldives', 'Netherlands', 'Sweden',
    'Switzerland', 'Norway', 'Denmark', 'Belgium', 'Austria', 'Russia',
    'China', 'South Korea', 'Brazil', 'Mexico', 'South Africa', 'Egypt',
    'Nigeria', 'Kenya', 'Thailand', 'Vietnam', 'Philippines', 'Indonesia',
    'Turkey', 'Israel', 'Greece', 'Portugal', 'Ireland', 'Poland', 'Finland',
    'Singapore', 'New Zealand', 'Pakistan', 'Bangladesh'
];

const SearchableSelect = ({ label, name, value, options, placeholder, onChange, required }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState(value || '');
    const containerRef = React.useRef(null);

    useEffect(() => {
        setSearchTerm(value || '');
    }, [value]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filteredOptions = options.filter(option =>
        option.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleSelect = (option) => {
        setSearchTerm(option);
        onChange({ target: { name, value: option } });
        setIsOpen(false);
    };

    const handleInputChange = (e) => {
        const val = e.target.value;
        setSearchTerm(val);
        onChange({ target: { name, value: val } });
        setIsOpen(true);
    };

    return (
        <div className="relative w-full" ref={containerRef}>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">
                {label} {required && <span className="text-indigo-500">*</span>}
            </label>
            <div className="relative">
                <input
                    type="text"
                    name={name}
                    value={searchTerm}
                    placeholder={placeholder}
                    autoComplete="off"
                    onFocus={() => setIsOpen(true)}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 bg-gray-50 text-gray-900 placeholder-gray-400 text-sm transition-all duration-200 focus:outline-none focus:border-indigo-500 focus:bg-white focus:shadow-[0_0_0_4px_rgba(99,102,241,0.1)] pr-10"
                />
                <div
                    className="absolute inset-y-0 right-0 flex items-center pr-3 cursor-pointer pointer-events-none"
                >
                    <FiChevronDown className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </div>

                {isOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto scrollbar-hide">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option, idx) => (
                                <div
                                    key={idx}
                                    onClick={() => handleSelect(option)}
                                    className={`px-4 py-3 cursor-pointer text-sm transition-colors hover:bg-indigo-50 ${option === value ? 'bg-indigo-50 font-medium text-indigo-700' : 'text-gray-700'
                                        }`}
                                >
                                    {option}
                                </div>
                            ))
                        ) : (
                            <div className="px-4 py-3 text-sm text-gray-500 italic">No matches found</div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const CheckoutPage = () => {
    const { cartItems, loading, fetchCartItems } = useCart();
    const [orderLoading, setOrderLoading] = useState(false);
    const [error, setError] = useState('');
    const { currency } = useCurrency();

    // Form data state
    const [shippingInfo, setShippingInfo] = useState({
        firstName: '',
        lastName: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'Sri Lanka', // Added default country
        phone: ''
    });

    const { authToken } = useAuth();
    const navigate = useNavigate();

    // Sync cart when authToken changes
    useEffect(() => {
        fetchCartItems();
    }, [authToken]);

    // Calculate totals
    const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const shipping = 450;
    const tax = 0;
    const total = subtotal + shipping + tax;

    // Track InitiateCheckout on checkout view
    useEffect(() => {
        if (cartItems && cartItems.length > 0) {
            trackInitiateCheckout(cartItems, total, currency);
        }
    }, [cartItems?.length]);

    // Handle form input changes
    const handleShippingChange = (e) => {
        setShippingInfo({
            ...shippingInfo,
            [e.target.name]: e.target.value
        });
    };

    // Payment processing - Using PayHere (Most popular in Sri Lanka)
    const processPayment = (orderId) => {
        return new Promise(async (resolve, reject) => {
            try {
                // Generate hash on backend for security
                const config = authToken ? {
                    headers: { Authorization: `Bearer ${authToken}` }
                } : {};

                const hashResponse = await axios.post('/api/payment/generate-hash', {
                    order_id: orderId,
                    amount: total.toFixed(2),
                    currency: "LKR"
                }, config);

                const { hash, merchant_id, sandbox } = hashResponse.data;

                // PayHere configuration
                const payment = {
                    sandbox: sandbox !== undefined ? sandbox : true, // Dynamically use sandbox mode based on backend setting
                    merchant_id: merchant_id,
                    return_url: `${window.location.origin}/order-success`,
                    cancel_url: `${window.location.origin}/checkout`,
                    notify_url: `${window.location.origin}/api/payment/notify`, // Replace with your public webhook URL in production!
                    order_id: orderId,
                    items: cartItems.map(item => item.name).join(', ').substring(0, 100),
                    amount: total.toFixed(2),
                    currency: "LKR",
                    hash: hash,
                    first_name: shippingInfo.firstName,
                    last_name: shippingInfo.lastName,
                    email: "customer@example.com", // Replace appropriately if user email is available
                    phone: shippingInfo.phone,
                    address: shippingInfo.address,
                    city: shippingInfo.city,
                    country: "Sri Lanka"
                };

                // PayHere checkout events
                window.payhere.onCompleted = function onCompleted(completedOrderId) {
                    console.log("Payment completed. OrderID:" + completedOrderId);
                    resolve(completedOrderId);
                };

                window.payhere.onDismissed = function onDismissed() {
                    console.log("Payment dismissed");
                    reject("Payment was canceled by user");
                };

                window.payhere.onError = function onError(error) {
                    console.log("Error:" + error);
                    reject(error);
                };

                // Open PayHere UI
                console.log('Triggering PayHere checkout modal...');
                window.payhere.startPayment(payment);
            } catch (error) {
                console.error("Failed to initialize payment:", error);
                reject("Failed to securely setup payment gateway.");
            }
        });
    };

    // Handle order submission
    const handlePlaceOrder = async (e) => {
        e.preventDefault();
        setOrderLoading(true);
        setError('');

        try {
            // Basic validation
            if (!shippingInfo.firstName || !shippingInfo.lastName || !shippingInfo.address || !shippingInfo.city) {
                throw new Error("Please fill in all required shipping information");
            }

            console.log('Starting order creation...');

            // Helper function to extract valid base MongoDB ObjectId
            const getCleanProductId = (item) => {
                const rawId = item.productId?._id || item.productId || item._id || '';
                const strId = typeof rawId === 'string' ? rawId : String(rawId);
                return strId.split('-')[0];
            };

            // Create order data with correct structure for backend
            const orderData = {
                orderItems: cartItems.map(item => ({
                    productId: getCleanProductId(item),
                    name: item.name,
                    price: item.price,
                    quantity: item.quantity,
                    image: item.image,
                    size: item.size,
                    color: item.color
                })),
                shippingAddress: {
                    firstName: shippingInfo.firstName,
                    lastName: shippingInfo.lastName,
                    address: shippingInfo.address,
                    city: shippingInfo.city,
                    state: shippingInfo.state,
                    zipCode: shippingInfo.zipCode || '00000', // Provide default if empty
                    country: shippingInfo.country,
                    phone: shippingInfo.phone
                },
                paymentMethod: 'PayHere',
                itemsPrice: subtotal,
                shippingPrice: shipping,
                taxPrice: tax,
                totalPrice: total
            };

            console.log('Order data being sent:', orderData);

            const config = authToken ? {
                headers: {
                    Authorization: `Bearer ${authToken}`,
                    'Content-Type': 'application/json'
                },
            } : {
                headers: { 'Content-Type': 'application/json' }
            };

            // Save order to database
            console.log('Sending order to backend...');
            const endpoint = authToken ? '/api/orders' : '/api/orders/guest';
            const orderResponse = await axios.post(endpoint, orderData, config);
            console.log('Order created successfully:', orderResponse.data);
            const orderId = orderResponse.data._id;

            // Process payment
            try {
                console.log('Processing payment...');
                await processPayment(orderId);
                console.log('Payment processed successfully');

                // If payment successful, update order status (optional - can be handled by webhook)
                // await axios.patch(`/api/orders/${orderId}`, { status: 'paid' }, config);

                // Clear cart
                console.log('Clearing cart...');
                if (authToken) {
                    await axios.delete('/api/users/cart', config); // Changed from /clear to match your route
                } else {
                    localStorage.removeItem('guestCart');
                    fetchCartItems();
                }
                console.log('Cart cleared successfully');

                // Redirect to success page
                navigate('/order-success', {
                    state: {
                        orderId: orderId,
                        orderTotal: total
                    }
                });

            } catch (paymentError) {
                console.error('Payment error:', paymentError);
                // If payment fails, you might want to update order status
                // await axios.patch(`/api/orders/${orderId}`, { status: 'failed' }, config);
                throw new Error("Payment processing failed: " + paymentError);
            }

        } catch (err) {
            console.error("Order submission error:", err);
            if (err.response) {
                console.error("Error response:", err.response.data);
                setError(err.response.data.message || "Failed to place order");
            } else {
                setError(err.message || "Failed to place order");
            }
        } finally {
            setOrderLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="bg-gray-50 py-12 min-h-screen">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
                        <p className="mt-4 text-gray-600">Loading checkout...</p>
                    </div>
                </div>
            </div>
        );
    }


    if (error && cartItems.length === 0) {
        return (
            <div className="text-center py-12">
                <p className="text-red-600 text-lg">{error}</p>
                <button
                    onClick={() => navigate('/cart')}
                    className="mt-4 bg-indigo-600 text-white py-2 px-6 rounded-md hover:bg-indigo-700"
                >
                    Go to Cart
                </button>
            </div>
        );
    }

    return (
        <div className="bg-gray-50 min-h-screen">
            <main className="max-w-7xl mx-auto pt-16 pb-24 px-4 sm:px-6 lg:px-8">
                <h1 className="text-3xl font-extrabold tracking-tight text-gray-900 text-center">Checkout</h1>

                {error && (
                    <div className="mt-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                        {error}
                    </div>
                )}

                <form onSubmit={handlePlaceOrder} className="mt-12 lg:grid lg:grid-cols-12 lg:gap-x-12 lg:items-start">
                    <section className="lg:col-span-7">
                        {/* Shipping Information */}
                        <div className="bg-white p-8 rounded-2xl shadow-md border border-gray-100">
                            <h2 className="text-xl font-semibold text-gray-900 mb-1">Shipping Information</h2>
                            <p className="text-sm text-gray-400 mb-6">Enter your delivery details below</p>
                            <div className="grid grid-cols-1 gap-y-5 sm:grid-cols-2 sm:gap-x-5">
                                <div className="flex flex-col gap-1">
                                    <label htmlFor="firstName" className="text-xs font-semibold text-gray-500 uppercase tracking-wide">First Name <span className="text-indigo-500">*</span></label>
                                    <input
                                        type="text"
                                        id="firstName"
                                        name="firstName"
                                        placeholder="John"
                                        required
                                        value={shippingInfo.firstName}
                                        onChange={handleShippingChange}
                                        className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 bg-gray-50 text-gray-900 placeholder-gray-400 text-sm transition-all duration-200 focus:outline-none focus:border-indigo-500 focus:bg-white focus:shadow-[0_0_0_4px_rgba(99,102,241,0.1)]"
                                    />
                                </div>
                                <div className="flex flex-col gap-1">
                                    <label htmlFor="lastName" className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Last Name <span className="text-indigo-500">*</span></label>
                                    <input
                                        type="text"
                                        id="lastName"
                                        name="lastName"
                                        placeholder="Doe"
                                        required
                                        value={shippingInfo.lastName}
                                        onChange={handleShippingChange}
                                        className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 bg-gray-50 text-gray-900 placeholder-gray-400 text-sm transition-all duration-200 focus:outline-none focus:border-indigo-500 focus:bg-white focus:shadow-[0_0_0_4px_rgba(99,102,241,0.1)]"
                                    />
                                </div>
                                <div className="flex flex-col gap-1 sm:col-span-2">
                                    <label htmlFor="address" className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Address <span className="text-indigo-500">*</span></label>
                                    <input
                                        type="text"
                                        id="address"
                                        name="address"
                                        placeholder="123 Flower Road"
                                        required
                                        value={shippingInfo.address}
                                        onChange={handleShippingChange}
                                        className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 bg-gray-50 text-gray-900 placeholder-gray-400 text-sm transition-all duration-200 focus:outline-none focus:border-indigo-500 focus:bg-white focus:shadow-[0_0_0_4px_rgba(99,102,241,0.1)]"
                                    />
                                </div>
                                <div className="flex flex-col gap-1">
                                    <label htmlFor="city" className="text-xs font-semibold text-gray-500 uppercase tracking-wide">City <span className="text-indigo-500">*</span></label>
                                    <input
                                        type="text"
                                        id="city"
                                        name="city"
                                        placeholder="Colombo"
                                        required
                                        value={shippingInfo.city}
                                        onChange={handleShippingChange}
                                        className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 bg-gray-50 text-gray-900 placeholder-gray-400 text-sm transition-all duration-200 focus:outline-none focus:border-indigo-500 focus:bg-white focus:shadow-[0_0_0_4px_rgba(99,102,241,0.1)]"
                                    />
                                </div>
                                <div className="flex flex-col gap-1">
                                    <SearchableSelect
                                        label="State/Province"
                                        name="state"
                                        value={shippingInfo.state}
                                        options={PROVINCES}
                                        placeholder="Select or type province"
                                        onChange={handleShippingChange}
                                    />
                                </div>
                                <div className="flex flex-col gap-1">
                                    <label htmlFor="zipCode" className="text-xs font-semibold text-gray-500 uppercase tracking-wide">ZIP Code</label>
                                    <input
                                        type="text"
                                        id="zipCode"
                                        name="zipCode"
                                        placeholder="00100"
                                        value={shippingInfo.zipCode}
                                        onChange={handleShippingChange}
                                        className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 bg-gray-50 text-gray-900 placeholder-gray-400 text-sm transition-all duration-200 focus:outline-none focus:border-indigo-500 focus:bg-white focus:shadow-[0_0_0_4px_rgba(99,102,241,0.1)]"
                                    />
                                </div>
                                <div className="flex flex-col gap-1">
                                    <label htmlFor="phone" className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Phone Number <span className="text-indigo-500">*</span></label>
                                    <input
                                        type="tel"
                                        id="phone"
                                        name="phone"
                                        placeholder="0712345678"
                                        required
                                        value={shippingInfo.phone}
                                        onChange={handleShippingChange}
                                        className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 bg-gray-50 text-gray-900 placeholder-gray-400 text-sm transition-all duration-200 focus:outline-none focus:border-indigo-500 focus:bg-white focus:shadow-[0_0_0_4px_rgba(99,102,241,0.1)]"
                                    />
                                </div>
                                <div className="flex flex-col gap-1 sm:col-span-2">
                                    <SearchableSelect
                                        label="Country"
                                        name="country"
                                        value={shippingInfo.country}
                                        options={COUNTRIES}
                                        placeholder="Select or type country"
                                        onChange={handleShippingChange}
                                    />
                                </div>
                            </div>
                        </div>


                    </section>

                    {/* Order Summary - Using Real Cart Data */}
                    <section className="mt-16 lg:mt-0 lg:col-span-5">
                        <div className="bg-white p-6 rounded-lg shadow-md">
                            <h2 className="text-lg font-medium text-gray-900">Order Summary</h2>
                            <ul className="divide-y divide-gray-200 mt-4">
                                {cartItems.map(item => (
                                    <li key={item._id} className="flex py-4 items-center justify-between">
                                        <div className="flex items-center">
                                            <img
                                                src={item.image || '/placeholder-image.png'}
                                                alt={item.name}
                                                className="w-16 h-16 object-cover rounded mr-4"
                                            />
                                            <div>
                                                <p className="font-medium">{item.name}</p>
                                                <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                                                {(item.size || item.color) && (
                                                    <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                                                        {item.color && (
                                                            <div className="flex items-center gap-1">
                                                                <span
                                                                    className="w-3 h-3 rounded-full border border-gray-300 inline-block"
                                                                    style={{ backgroundColor: getColorCode(item.color) }}
                                                                    title={item.color}
                                                                />
                                                                {item.color}
                                                            </div>
                                                        )}
                                                        {item.color && item.size && <span>/</span>}
                                                        {item.size && <span>{item.size}</span>}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <p className="font-medium">{formatCurrency(item.price * item.quantity, currency)}</p>
                                    </li>
                                ))}
                            </ul>
                            <div className="border-t border-gray-200 pt-4 mt-4 space-y-2">
                                <div className="flex justify-between text-sm">
                                    <p>Subtotal</p>
                                    <p>{formatCurrency(subtotal, currency)}</p>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <p>Shipping</p>
                                    <p>{formatCurrency(shipping, currency)}</p>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <p>Tax</p>
                                    <p>{formatCurrency(tax, currency)}</p>
                                </div>
                                <div className="flex justify-between text-base font-medium border-t pt-2">
                                    <p>Total</p>
                                    <p>{formatCurrency(total, currency)}</p>
                                </div>
                            </div>
                            <button
                                type="submit"
                                disabled={orderLoading}
                                className={`w-full mt-6 py-3 rounded-md text-white font-medium ${orderLoading
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-indigo-600 hover:bg-indigo-700'
                                    }`}
                            >
                                {orderLoading ? 'Processing...' : 'Place Order'}
                            </button>
                        </div>
                    </section>
                </form>
            </main>
        </div>
    );
};

export default CheckoutPage;